import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { claimProspectAccount } from '../lib/admin'
import { useAuth } from '../lib/auth'
import Wordmark from '../components/Wordmark'

/**
 * The other end of the claim link.
 *
 * Whoever opens it has to be signed in first, because the account binds to a
 * uid and there isn't one until they are. Signing up is the normal flow — the
 * token survives the round trip in the URL — and then this transfers
 * everything that was built for them.
 */
export default function Claim() {
  const [params] = useSearchParams()
  const { user, initialising } = useAuth()
  const navigate = useNavigate()

  const token = params.get('token') ?? ''
  const [state, setState] = useState<'idle' | 'working' | 'done' | 'failed'>('idle')
  const [error, setError] = useState<string | null>(null)
  const [moved, setMoved] = useState(0)

  useEffect(() => {
    if (!token) {
      setState('failed')
      setError('That link is missing its token.')
    }
  }, [token])

  const claim = async () => {
    setState('working')
    try {
      const result = await claimProspectAccount({ token })
      setMoved(result.toursTransferred)
      setState('done')
    } catch (caught) {
      setError((caught as Error).message)
      setState('failed')
    }
  }

  if (initialising) return null

  return (
    <div
      style={{
        display: 'grid',
        placeItems: 'center',
        minHeight: '100dvh',
        padding: 24,
        background: 'var(--bg)',
      }}
    >
      <div className="panel panel-pad stack" style={{ maxWidth: 460, padding: 32, gap: 14 }}>
        <Wordmark />

        {state === 'done' ? (
          <>
            <h1 className="t-display-s">It's yours</h1>
            <p className="t-caption">
              {moved} tour{moved === 1 ? '' : 's'} moved across, along with the artists
              and everything attached to them.
            </p>
            <button
              className="btn btn-primary"
              onClick={() => navigate('/app')}
              style={{ alignSelf: 'flex-start' }}
            >
              Open Encore
            </button>
          </>
        ) : !user ? (
          <>
            <h1 className="t-display-s">Claim your account</h1>
            <p className="t-caption">
              Sign in or create an account with any email, and everything we've set up
              becomes yours. The link stays valid while you do.
            </p>
            <button
              className="btn btn-primary"
              onClick={() =>
                navigate(`/app/signin?next=${encodeURIComponent(`/app/claim?token=${token}`)}`)
              }
              style={{ alignSelf: 'flex-start' }}
            >
              Continue
            </button>
          </>
        ) : (
          <>
            <h1 className="t-display-s">Claim your account</h1>
            <p className="t-caption">
              Signed in as {user.email}. Claiming moves the artists and tours we've set
              up into this account.
            </p>
            {error && (
              <p className="t-caption" style={{ color: 'var(--danger)' }}>
                {error}
              </p>
            )}
            <button
              className="btn btn-primary"
              onClick={claim}
              disabled={state === 'working' || !token}
              style={{ alignSelf: 'flex-start' }}
            >
              {state === 'working' ? 'Claiming…' : 'Claim it'}
            </button>
          </>
        )}
      </div>
    </div>
  )
}
